import type {
  ClientMergePreview,
  ClientMergeSide,
  DuplicateMatchReason,
  MergeClientsRequest,
} from "@planerius/contracts/cabinet"

/**
 * ДУБЛИ И СЛИЯНИЕ (ТЗ 03, 3-А7; макет «Каталог и клиенты - П2») — правила без вёрстки.
 *
 * Что выбирает администратор: какая карточка главная, чьи имя, дата рождения, пол и язык
 * остаются и какой телефон станет основным. Чего он НЕ выбирает: телефоны, каналы, заметки,
 * метки и визиты объединяются все, счётчики пересчитываются, согласия — по «меньше прав».
 * Экран показывает это словами, иначе человек ищет, где выбрать «сколько было визитов».
 */

export type Side = "left" | "right"

export const REASON_LABEL: Readonly<Record<DuplicateMatchReason, string>> = {
  phone: "Совпадает телефон",
  name_birthdate: "Имя и дата рождения",
  name_created_close: "Имя и дата создания",
}

/** Значок силы совпадения: телефон — сильный признак, имя — повод посмотреть. */
export const REASON_TONE: Readonly<Record<DuplicateMatchReason, "error" | "warn">> = {
  phone: "error",
  name_birthdate: "warn",
  name_created_close: "warn",
}

export const CHOICE_FIELDS = ["displayName", "birthDate", "gender", "locale"] as const
export type ChoiceField = (typeof CHOICE_FIELDS)[number]

export const FIELD_LABEL: Readonly<Record<ChoiceField, string>> = {
  displayName: "Имя",
  birthDate: "Дата рождения",
  gender: "Пол",
  locale: "Язык общения",
}

const GENDER: Readonly<Record<ClientMergeSide["gender"], string>> = {
  female: "женский",
  male: "мужской",
  other: "другой",
  unknown: "не указан",
}

const LOCALE: Readonly<Record<string, string>> = { ru: "русский", en: "английский", kk: "казахский" }

export interface MergeForm {
  readonly main: Side
  readonly choices: Readonly<Record<ChoiceField, Side>>
  readonly primaryPhoneId: string | null
}

export function other(side: Side): Side {
  return side === "left" ? "right" : "left"
}

export function sideOf(preview: ClientMergePreview, side: Side): ClientMergeSide {
  return side === "left" ? preview.left : preview.right
}

function filled(card: ClientMergeSide, field: ChoiceField): boolean {
  switch (field) {
    case "displayName":
      return card.displayName.trim() !== ""
    case "birthDate":
      return card.birthDate !== null
    case "gender":
      return card.gender !== "unknown"
    case "locale":
      return true
  }
}

/**
 * Главная — карточка с бо́льшей историей (при равенстве — более старая): в неё переедет
 * меньше строк, и «вы обычно у Ани» останется верным. Поле берётся у главной, кроме
 * случая, когда у неё пусто, а у второй заполнено, — пустое значение не выигрывает.
 */
export function defaultForm(preview: ClientMergePreview): MergeForm {
  const { left, right } = preview
  const main: Side =
    left.visitsCount !== right.visitsCount
      ? left.visitsCount > right.visitsCount
        ? "left"
        : "right"
      : left.createdAt <= right.createdAt
        ? "left"
        : "right"
  return withMain(preview, main)
}

export function withMain(preview: ClientMergePreview, main: Side): MergeForm {
  const mainCard = sideOf(preview, main)
  const otherCard = sideOf(preview, other(main))
  const choices = {} as Record<ChoiceField, Side>
  for (const field of CHOICE_FIELDS) {
    choices[field] = !filled(mainCard, field) && filled(otherCard, field) ? other(main) : main
  }
  const primary = mainCard.phones.find((phone) => phone.isPrimary) ?? mainCard.phones[0]
  return { main, choices, primaryPhoneId: primary?.id ?? null }
}

/** Что можно сделать основным: телефоны главной и те номера второй, которых у главной нет. */
export function primaryCandidates(preview: ClientMergePreview, main: Side): ClientMergeSide["phones"] {
  return [
    ...sideOf(preview, main).phones,
    ...sideOf(preview, other(main)).phones.filter((phone) => !phone.alsoInOther),
  ]
}

export function valueOf(card: ClientMergeSide, field: ChoiceField): string {
  switch (field) {
    case "displayName":
      return card.displayName
    case "birthDate":
      return card.birthDate === null ? "—" : dateLabel(`${card.birthDate}T12:00:00Z`, "UTC")
    case "gender":
      return GENDER[card.gender]
    case "locale":
      return LOCALE[card.locale] ?? card.locale
  }
}

/** «12 апреля 1990» — без «г.». */
export function dateLabel(instant: string, timeZone: string): string {
  const parsed = new Date(instant)
  if (Number.isNaN(parsed.getTime())) return "—"
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric", timeZone })
    .format(parsed)
    .replace(/\s?г\.$/, "")
}

/** Запрос слияния из формы: главная — выжившая, вторая — слитая. */
export function requestOf(preview: ClientMergePreview, form: MergeForm): MergeClientsRequest | null {
  if (form.primaryPhoneId === null) return null
  const survivor = sideOf(preview, form.main)
  const merged = sideOf(preview, other(form.main))
  const pick = (field: ChoiceField): "survivor" | "merged" => (form.choices[field] === form.main ? "survivor" : "merged")
  return {
    survivorId: survivor.id,
    mergedId: merged.id,
    fieldChoices: {
      displayName: pick("displayName"),
      // Имя и фамилия отдельно идут вместе с отображаемым именем: «Иванова Мария» с фамилией
      // от второй карточки — это третий человек.
      firstName: pick("displayName"),
      lastName: pick("displayName"),
      birthDate: pick("birthDate"),
      gender: pick("gender"),
      locale: pick("locale"),
    },
    primaryPhoneId: form.primaryPhoneId,
    expectedSurvivorVersion: survivor.version,
    expectedMergedVersion: merged.version,
  }
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`
  return `${n} ${many}`
}

export const phonesWord = (n: number): string => plural(n, "телефон", "телефона", "телефонов")
export const visitsWord = (n: number): string => plural(n, "визит", "визита", "визитов")
export const noShowWord = (n: number): string => plural(n, "неявка", "неявки", "неявок")
export const pairsWord = (n: number): string => plural(n, "пару", "пары", "пар")

/** Рекламное согласие после слияния — «меньше прав»: отозванное в любой карточке побеждает. */
export function adsAfter(preview: ClientMergePreview): "given" | "revoked" | "never" {
  const states = new Set([preview.left.adsConsent, preview.right.adsConsent])
  if (states.has("revoked")) return "revoked"
  if (states.has("given")) return "given"
  return "never"
}

export interface MergeSummary {
  readonly name: string
  readonly phones: number
  readonly visits: number
  readonly noShows: number
  readonly tags: readonly string[]
  readonly ads: "given" | "revoked" | "never"
  readonly blacklisted: boolean
}

/** Предпросмотр результата — обязателен по ТЗ: «останется одна карточка: …». */
export function summaryOf(preview: ClientMergePreview, form: MergeForm): MergeSummary {
  const nameSide = sideOf(preview, form.choices.displayName)
  return {
    name: nameSide.displayName,
    phones: primaryCandidates(preview, form.main).length,
    visits: preview.left.visitsCount + preview.right.visitsCount,
    noShows: preview.left.noShowCount + preview.right.noShowCount,
    tags: [...new Set([...preview.left.tags, ...preview.right.tags])].toSorted((a, b) => a.localeCompare(b, "ru")),
    ads: adsAfter(preview),
    blacklisted: preview.left.blacklisted || preview.right.blacklisted,
  }
}

/**
 * Что изменилось, пока выбирали поля (ТЗ 3-А7, «Конфликт»): «в карточку «Иванова М.»
 * добавили запись на 18 сентября». Слияние в такой ситуации не выполняется молча —
 * человек мог выбирать поля, исходя из старого состава.
 */
export function changeText(before: ClientMergePreview, after: ClientMergePreview, timeZone: string): string | null {
  const parts: string[] = []
  for (const side of ["left", "right"] as const) {
    const was = sideOf(before, side)
    const now = sideOf(after, side)
    if (was.version === now.version) continue
    if (now.appointmentsCount > was.appointmentsCount && now.latestBookedStartAt !== null) {
      parts.push(`в карточку «${now.displayName}» добавили запись на ${dayMonth(now.latestBookedStartAt, timeZone)}`)
    } else if (now.phones.length > was.phones.length) {
      parts.push(`в карточку «${now.displayName}» добавили телефон`)
    } else {
      parts.push(`карточку «${now.displayName}» изменили`)
    }
  }
  if (parts.length === 0) return null
  return `Пока вы выбирали, ${parts.join(", а ")}. Посмотрите обновлённый состав и подтвердите слияние ещё раз.`
}

function dayMonth(instant: string, timeZone: string): string {
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", timeZone }).format(new Date(instant))
}

/** Сводка карточки в строке списка дублей: «+7 916… · 14 визитов · создана 03.2024». */
export function pairSub(
  card: { readonly phonesMasked: readonly string[]; readonly visitsCount: number; readonly lastVisitAt: string | null; readonly createdAt: string },
  timeZone: string,
): string {
  const created = new Intl.DateTimeFormat("ru-RU", { month: "2-digit", year: "numeric", timeZone }).format(
    new Date(card.createdAt),
  )
  return [
    card.phonesMasked[0] ?? "без телефона",
    visitsWord(card.visitsCount),
    card.lastVisitAt === null ? null : `последний ${dayMonth(card.lastVisitAt, timeZone)}`,
    `создана ${created}`,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ")
}
